import { observer } from "mobx-react-lite";
import React from "react";
import ThumbnailImage from "../../../components-new/Thumbnail";
import { useStore } from "../../../helpers";
import { useIsMobile } from "../../../custom-hooks";
import { useTranslation } from "react-i18next";

export const SrAdditionalInfo = observer(() => {
    const rootStore = useStore();
    const { serviceRequestsStore, userStore } = rootStore;
    const isMobile = useIsMobile();
    const { activeServiceRequest } = serviceRequestsStore;
    const { t } = useTranslation("service_request_home");

    const attachments = activeServiceRequest?.more?.attachments || [];

    const handleAttachmentClick = async (attachment: any) => {
        if (attachment.type?.startsWith("image/")) {
            userStore.set_attachment_image({
                s3Key: attachment.path,
                bucket: "urn-assets",
                width: isMobile ? 300 : 800,
                height: isMobile ? 300 : 600,
            });
            await userStore.update_thumbnail_modal(true);
        }
    };

    const isImage = (fileName: string) => /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(fileName);

    const handleDownload = async (attachment: any) => {
        try {
            const fileUrl = attachment.url || `https://urn-assets.s3.amazonaws.com/${attachment.path}`;
            const response = await fetch(fileUrl, { method: "GET" });

            if (!response.ok) throw new Error("Download failed");

            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = attachment.name || "download";
            document.body.appendChild(link);
            link.click();

            // cleanup
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error("Download error:", error);
            alert("Failed to download the file. Please try again.");
        }
    };

    return (
        <div className="m-4 flex flex-col gap-4">
            <div className="flex flex-col rounded-xl bg-background-info p-4">
                <p className="pb-2 font-semibold text-common-black">{t("additional_information")}</p>
                <p>{activeServiceRequest?.more?.additional || t("no_additional_info")}</p>
                {activeServiceRequest?.more?.is_fund_required && (
                    <div className="mt-4 rounded-lg bg-white p-4">
                        <p className="mb-3 font-semibold text-common-black">Funds Information</p>

                        <div className="grid grid-cols-2 gap-y-3 text-sm">
                            <span className="text-gray-500">Funds Type</span>
                            <span className="text-right font-medium">
                                {activeServiceRequest?.more?.funds_type?.name || "-"}
                            </span>

                            <span className="text-gray-500">Amount</span>
                            <span className="text-right font-semibold">
                                ₹
                                {Number(activeServiceRequest?.more?.funds_amount || 0).toLocaleString(
                                    "en-IN"
                                )}
                            </span>
                        </div>
                    </div>
                )}

                {activeServiceRequest?.more?.rtc_details && (
                    <div className="mt-4 rounded-lg bg-white p-4">
                        <p className="mb-3 font-semibold text-common-black">RTC Details</p>

                        <div className="grid grid-cols-2 gap-y-3 text-sm">
                            <span className="text-gray-500">RTC Details</span>

                            <span className="break-words text-right font-medium text-common-black">
                                {activeServiceRequest.more.rtc_details}
                            </span>
                        </div>
                    </div>
                )}
            </div>

            <div className="flex flex-col rounded-xl bg-background-info p-4">
                <p className="pb-2 font-semibold text-common-black">{t("uploaded_files")}</p>

                {attachments.length === 0 && <p className="text-gray-500">{t("no_attachments_uploaded")}</p>}

                <div className="flex flex-wrap gap-4">
                    {attachments
                        .filter((doc) => isImage(doc.name))
                        .map((doc, index) => (
                            <div
                                key={`${doc.path}-${index}`}
                                className="group relative h-[90px] w-[120px] cursor-pointer overflow-hidden rounded-lg border transition-all hover:shadow-md"
                            >
                                <div onClick={() => handleAttachmentClick(doc)}>
                                    <ThumbnailImage
                                        s3Key={doc.path}
                                        bucket="urn-assets"
                                        width={120}
                                        height={90}
                                        fit="FILL"
                                    />
                                </div>

                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDownload(doc);
                                    }}
                                    className="absolute bottom-2 right-2 rounded-full bg-white/80 p-1 opacity-0 transition-opacity group-hover:opacity-100"
                                    title="Download"
                                >
                                    <i className="fas fa-download text-sm text-gray-700"></i>
                                </button>
                            </div>
                        ))}
                </div>

                <div className="mt-2 flex flex-col gap-2">
                    {attachments
                        .filter((doc) => !isImage(doc.name))
                        .map((doc, index) => (
                            <div
                                key={`${doc.path}-${index}`}
                                className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 p-2 transition-all hover:bg-gray-100"
                            >
                                <div className="flex items-center gap-3 truncate">
                                    <i className="fas fa-file text-lg text-gray-600" />
                                    <p className="max-w-[180px] truncate text-sm font-semibold">{doc.name}</p>
                                </div>
                                <button
                                    onClick={() => handleDownload(doc)}
                                    className="text-gray-600 hover:text-gray-800"
                                    title="Download"
                                >
                                    <i className="fas fa-download text-lg"></i>
                                </button>
                            </div>
                        ))}
                </div>
            </div>
        </div>
    );
});
